// screens/Home.tsx
import React, { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Button, Platform, StyleSheet, View } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { NavigationContainer } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { useDispatch, useSelector } from 'react-redux';
import { resetState, updateState } from '../redux/services/authSlice';
import { RootState } from '../redux/store';

import DrawerLayout from '../Components/DrawerLayout';
import useInactivityLogout from '../hooks/useInactivityLogout';
import { ShowToast } from '../Common/common';
import { sseClient } from '../services/sseClient';

import DashboardFO from './DashboardFO';
import InHouse from './InHouse';
import RevenueSummary from './RevenueSummary';
import ReportCharts from './ReportCharts';
import RoomAvailability from './RoomAvailability';
import StatusScreen from './StatusScreen';
import AdvanceForm from './AdvanceForm';
import CheckInForm from './CheckInForm';
import CheckOutForm from './CheckOutForm';
import HouseKeeping from './HouseKeeping';
import PostingForm from './PostingForm';
import RoomShiftForm from './RoomShiftForm';
import Dashboard from './Dashboard';
import HomeScreen from './HomeScreen';
import Login from './Login';
import ForcePasswordChange from './ForcePasswordChange';
import Notifications from './Notifications';
import POSBill from './POSBill';
import POSCancelKOT from './POSCancelKOT';
import POSChangeTable from './POSChangeTable';
import POSProducts from './POSProducts';
import POSProductsBlock from './POSProductsBlock';
import POSRunningOrders from './POSRunningOrders';
import POSSettlement from './POSSettlement';
import POSTables from './POSTables';
import STIndent from './STIndent';
import STPurchase from './STPurchase';
import STPurchaseOrder from './STPurchaseOrder';
import FPProductionEntry from './FPProductionEntry';
import FPProductionList from './FPProductionList';
import PendingAuthorizations from './PendingAuthorizations';
import LinkRooms from './LinkRooms';
import AssetHome from './AssetHome';
import AmAssetList from './AmAssetList';
import AmAssetRegister from './AmAssetRegister';
import AmAssetEntry from './AmAssetEntry';
import AmCategoryList from './AmCategoryList';
import AmProductList from './AmProductList';
import AmSeedCatalogue from './AmSeedCatalogue';
import AmAssetDetail from './AmAssetDetail';
import AmAssetPurchaseEntry from './AmAssetPurchaseEntry';
import HelpScreen from './Help';
import PublicPlacesScreen from './PublicPlacesScreen';
import HKStatusScreen from './HKStatusScreen';
import HKProducts from './HKProducts';
import STProducts from './STProducts';
import { playSoundAlert } from '../utils/soundAlert';
import { foauthorizationApi } from '../redux/services/foauthorizationApi';
import { notificationsApi } from '../redux/services/anotificationsApi';

const Stack = createNativeStackNavigator();

const Home = () => {
  const navigationRef = useRef<any>(null);
  // Holds the DrawerLayout's reactive canGoBack updater,
  // wired in via onNavigationStateChange prop below.
  const navStateListenerRef = useRef<(() => void) | null>(null);
  const dispatch = useDispatch();
  const { user, username, company, token, mustChangePwd } = useSelector((state: RootState) => state.gdata);
  const glogic = useSelector((state: RootState) => state.glogic.glogic);
  const hasShownWelcomeRef = useRef(false);
  const previousUserRef = useRef<string>('');


  // On native, the persisted redux state is never rehydrated at startup
  // (persist.ts's loadLastState() intentionally returns undefined there,
  // since AsyncStorage is async) - so without this, every app restart loses
  // the session and forces a fresh login even though a still-valid token is
  // sitting in AsyncStorage. Restore it here before deciding Login vs Home.
  // (Web already gets its persisted state synchronously via preloadedState
  // in redux/store/index.ts, so this only needs to run on native.)
  const [hydrating, setHydrating] = useState(Platform.OS !== 'web');

  useEffect(() => {
    if (Platform.OS === 'web') {
      return;
    }
    (async () => {
      try {
        const stored = await AsyncStorage.getItem('authData');
        if (stored) {
          const parsed = JSON.parse(stored);
          if (parsed?.user && parsed?.token) {
            dispatch(updateState(parsed));
          }
        }
      } catch (error) {
        console.error('Failed to restore persisted session:', error);
      } finally {
        setHydrating(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SSE Connection
  useEffect(() => {
    if (user && company) {
      sseClient.connect(company, token);

      const unsubscribe = sseClient.addListener(async data => {
        if (data.type === 'SYSTEM_DATE_UPDATED') {
          // Automatic logout when system date changes
          ShowToast('System date has changed. Logging out...', 'info');
          dispatch(resetState());
        } else if (
          data.type === 'AUTHORIZATION_UPDATED' &&
          data.payload?.action === 'REQUESTED'
        ) {
          await playSoundAlert();
          ShowToast('New authorization request received', 'info');
          dispatch(foauthorizationApi.util.invalidateTags(['FOAuthorization']));
          dispatch(notificationsApi.util.invalidateTags(['ANotifications']));
        } else if (
          data.type === 'AUTHORIZATION_UPDATED' &&
          (data.payload?.action === 'APPROVED' ||
            data.payload?.action === 'REJECTED')
        ) {
          dispatch(foauthorizationApi.util.invalidateTags(['FOAuthorization']));
          dispatch(notificationsApi.util.invalidateTags(['ANotifications']));
        } else if (data.type === 'NOTIFICATION_ADDED') {
          await playSoundAlert();
          ShowToast('New notification received', 'info');
          dispatch(notificationsApi.util.invalidateTags(['ANotifications']));
          dispatch(foauthorizationApi.util.invalidateTags(['FOAuthorization']));
        }
      });

      return () => {
        unsubscribe();
      };
    } else {
      sseClient.disconnect();
    }
  }, [user, company, token, dispatch]);

  // Calculate logout timeout similar to UI
  // Default: 30 minutes (1800000ms)
  // If balanceAmount > 0 and daydiff > 60 and isLicenseDateExpired: 30 seconds (30000ms)
  const logouttime =
    glogic?.balanceAmount > 0 &&
    glogic?.daydiff > 60 &&
    glogic?.isLicenseDateExpired
      ? 30000
      : 1800000;

  // Auto logout hook
  useInactivityLogout(logouttime);

  // Show welcome toast after login (backup in case Login component toast doesn't show)
  useEffect(() => {
    // Check if user just logged in (transition from no user to having a user)
    const justLoggedIn = !previousUserRef.current && user && username;

    if (justLoggedIn && !hasShownWelcomeRef.current) {
      // Add a small delay to ensure Redux state is fully updated
      const timer = setTimeout(() => {
        if (username) {
          const welcomeMessage = `Welcome, ${username}!`;
          ShowToast(welcomeMessage, 'success');
          hasShownWelcomeRef.current = true;
        }
      }, 100);

      return () => clearTimeout(timer);
    }

    // Reset when user logs out
    if (!user) {
      hasShownWelcomeRef.current = false;
    }

    // Update previous user reference
    previousUserRef.current = user || '';
  }, [user, username]);

  if (hydrating) {
    return (
      <View style={styles.hydratingContainer}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  if (!user) return <Login />;
  if (mustChangePwd) return <ForcePasswordChange />;

  return (
    <>
      <NavigationContainer
        ref={navigationRef}
        onStateChange={() => {
          // Notify DrawerLayout to re-evaluate canGoBack after every navigation transition
          navStateListenerRef.current?.();
        }}>
        <DrawerLayout
          navigationRef={navigationRef}
          onNavigationStateChange={(updater: () => void) => {
            navStateListenerRef.current = updater;
          }}>
          {/* React Navigation v7 requires an explicit `id` when using the
              dynamic (non-static) Navigator API this app is built on -
              `undefined` is the documented value for "no static config id". */}
          <Stack.Navigator id={undefined} screenOptions={{ headerShown: false }}>
            <Stack.Screen name="Dashboard" component={DashboardFO} />
            <Stack.Screen name="StatusScreen" component={StatusScreen} />
            <Stack.Screen
              name="RoomAvailability"
              component={RoomAvailability}
            />
            <Stack.Screen name="InHouse" component={InHouse} />
            <Stack.Screen name="RevenueSummary" component={RevenueSummary} />
            <Stack.Screen name="ReportCharts" component={ReportCharts} />
            <Stack.Screen name="HouseKeeping" component={HouseKeeping} />
            <Stack.Screen name="Checkin" component={CheckInForm} />
            <Stack.Screen name="Posting" component={PostingForm} />
            <Stack.Screen name="Advance" component={AdvanceForm} />
            <Stack.Screen name="RoomShift" component={RoomShiftForm} />
            <Stack.Screen name="Checkout" component={CheckOutForm} />
            <Stack.Screen name="Home" component={HomeScreen} />
            <Stack.Screen name="Tables" component={POSTables} />
            <Stack.Screen name="Products" component={POSProducts} />
            <Stack.Screen name="CancelKOT" component={POSCancelKOT} />
            <Stack.Screen name="ChangeTable" component={POSChangeTable} />
            <Stack.Screen name="Bill" component={POSBill} />
            <Stack.Screen name="Settlement" component={POSSettlement} />
            <Stack.Screen name="DashboardPOS" component={Dashboard} />
            <Stack.Screen name="RunningOrders" component={POSRunningOrders} />
            <Stack.Screen name="ProductBlock" component={POSProductsBlock} />
            <Stack.Screen name="PurchaseOrder" component={STPurchaseOrder} />
            <Stack.Screen name="Purchase" component={STPurchase} />
            <Stack.Screen name="Indent" component={STIndent} />
            <Stack.Screen
              name="FPProductionEntry"
              component={FPProductionEntry}
            />
            <Stack.Screen name="Notifications" component={Notifications} />
            <Stack.Screen
              name="PendingAuthorizations"
              component={PendingAuthorizations}
            />
            <Stack.Screen name="LinkRooms" component={LinkRooms} />
            <Stack.Screen name="AssetHome" component={AssetHome} />
            <Stack.Screen name="AmAssetList" component={AmAssetList} />
            <Stack.Screen name="AmAssetRegister" component={AmAssetRegister} options={{ title: "Asset Register" }} />
            <Stack.Screen name="AmAssetEntry" component={AmAssetEntry} options={{ title: "New Asset" }} />
            <Stack.Screen name="AmCategoryList" component={AmCategoryList} options={{ title: "Asset Categories" }} />
            <Stack.Screen name="AmProductList" component={AmProductList} options={{ title: "Asset Products" }} />
            <Stack.Screen name="AmSeedCatalogue" component={AmSeedCatalogue} options={{ title: "Seed Catalogue" }} />
            <Stack.Screen name="AmAssetDetail" component={AmAssetDetail} options={{ title: "Asset Details" }} />
            <Stack.Screen name="AmAssetPurchaseEntry" component={AmAssetPurchaseEntry} />
            <Stack.Screen name="Help" component={HelpScreen} />
            <Stack.Screen name="PublicPlacesScreen" component={PublicPlacesScreen} />
            <Stack.Screen name="HKStatusScreen" component={HKStatusScreen} />
            <Stack.Screen name="HKProducts" component={HKProducts} />
            <Stack.Screen name="STProducts" component={STProducts} />
          </Stack.Navigator>
        </DrawerLayout>
      </NavigationContainer>
    </>
  );
};

const styles = StyleSheet.create({
  hydratingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default Home;
